import { db } from './firebase';
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  orderBy,
  limit as firestoreLimit,
} from 'firebase/firestore';

// Types for Stripe data
export interface PaymentMethod {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created: Date;
  invoiceUrl?: string;
  invoicePdf?: string;
}

// Note: NO Stripe.js or publishable key needed!
// The firestore-stripe-payments extension handles checkout session creation
// and returns hosted checkout URLs directly from Stripe.

// Stripe price IDs from environment variables (or Firestore products collection)
// Legacy single-price format (for backwards compatibility)
export const PRICE_IDS = {
  basic: import.meta.env.VITE_STRIPE_CORE_PRICE_ID,
  pro: import.meta.env.VITE_STRIPE_MORE_PRICE_ID,
  enterprise: import.meta.env.VITE_STRIPE_COMPLETE_PRICE_ID,
};

// Extended price IDs with monthly/annual variants
export const PLAN_PRICE_IDS = {
  core: {
    monthly: import.meta.env.VITE_STRIPE_CORE_MONTHLY_PRICE_ID || import.meta.env.VITE_STRIPE_CORE_PRICE_ID,
    annual: import.meta.env.VITE_STRIPE_CORE_ANNUAL_PRICE_ID,
  },
  more: {
    monthly: import.meta.env.VITE_STRIPE_MORE_MONTHLY_PRICE_ID || import.meta.env.VITE_STRIPE_MORE_PRICE_ID,
    annual: import.meta.env.VITE_STRIPE_MORE_ANNUAL_PRICE_ID,
  },
  complete: {
    monthly: import.meta.env.VITE_STRIPE_COMPLETE_MONTHLY_PRICE_ID || import.meta.env.VITE_STRIPE_COMPLETE_PRICE_ID,
    annual: import.meta.env.VITE_STRIPE_COMPLETE_ANNUAL_PRICE_ID,
  },
};

// Add-on price IDs
export const ADDON_PRICE_IDS = {
  warehouse: import.meta.env.VITE_STRIPE_ADDON_WAREHOUSE_PRICE_ID,
  manufacture: import.meta.env.VITE_STRIPE_ADDON_MANUFACTURE_PRICE_ID,
  extraOrders: import.meta.env.VITE_STRIPE_ADDON_EXTRA_ORDERS_PRICE_ID,
};

/**
 * Creates a checkout session using the firestore-stripe-payments extension
 * 
 * How it works (NO BACKEND NEEDED):
 * 1. Write document to customers/{userId}/checkout_sessions
 * 2. Extension creates Stripe checkout session server-side
 * 3. Extension updates document with 'url' field (hosted checkout page)
 * 4. Client redirects to that URL
 * 
 * @param userId - The authenticated user's ID
 * @param priceId - The Stripe price ID for the selected plan
 * @param successUrl - URL to redirect to on successful payment
 * @param cancelUrl - URL to redirect to on cancelled payment
 */
export async function createCheckoutSession(
  userId: string,
  priceId: string,
  successUrl: string,
  cancelUrl: string
) {
  try {
    // Create a checkout session document in Firestore
    // The firestore-stripe-payments extension watches this collection
    const checkoutSessionRef = collection(
      db,
      `customers/${userId}/checkout_sessions`
    );

    const docRef = await addDoc(checkoutSessionRef, {
      price: priceId,
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
      allow_promotion_codes: true, // Enable promo codes
      trial_period_days: 14, // 14-day free trial
    });

    // Wait for the extension to populate the checkout URL
    return new Promise<string>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        docRef,
        (snap) => {
          const data = snap.data();
          if (data?.error) {
            unsubscribe();
            reject(new Error(data.error.message));
          }
          // Extension populates 'url' field with Stripe Checkout URL
          if (data?.url) {
            unsubscribe();
            resolve(data.url);
          }
        },
        reject
      );
      
      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        reject(new Error('Timeout waiting for checkout session URL from extension'));
      }, 10000);
    });
  } catch (error: any) {
    throw new Error(`Failed to create checkout session: ${error.message}`);
  }
}

/**
 * Creates a subscription checkout session with multiple line items
 * Supports plan + add-ons in a single checkout
 *
 * @param userId - The authenticated user's ID
 * @param lineItems - Array of { price: priceId, quantity: number }
 * @param successUrl - URL to redirect to on successful payment
 * @param cancelUrl - URL to redirect to on cancelled payment
 * @param metadata - Optional metadata to attach to the subscription
 */
export async function createSubscriptionCheckout(
  userId: string,
  lineItems: Array<{ price: string; quantity?: number }>,
  successUrl: string,
  cancelUrl: string,
  metadata?: Record<string, string>
) {
  try {
    const checkoutSessionRef = collection(
      db,
      `customers/${userId}/checkout_sessions`
    );

    const sessionData: Record<string, any> = {
      line_items: lineItems.map((item) => ({
        price: item.price,
        quantity: item.quantity || 1,
      })),
      success_url: successUrl,
      cancel_url: cancelUrl,
      mode: 'subscription',
      allow_promotion_codes: true,
      trial_period_days: 14,
    };

    if (metadata) {
      sessionData.metadata = metadata;
    }

    const docRef = await addDoc(checkoutSessionRef, sessionData);

    // Wait for the extension to populate the checkout URL
    return new Promise<string>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        docRef,
        (snap) => {
          const data = snap.data();
          if (data?.error) {
            unsubscribe();
            reject(new Error(data.error.message));
          }
          if (data?.url) {
            unsubscribe();
            resolve(data.url);
          }
        },
        reject
      );

      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        reject(
          new Error('Timeout waiting for checkout session URL from extension')
        );
      }, 10000);
    });
  } catch (error: any) {
    throw new Error(`Failed to create subscription checkout: ${error.message}`);
  }
}

/**
 * Gets the user's active subscriptions
 * Reads from the customers/{userId}/subscriptions collection managed by the extension
 */
export async function getUserSubscriptions(userId: string) {
  try {
    const subscriptionsRef = collection(db, `customers/${userId}/subscriptions`);
    const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing']));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error: any) {
    throw new Error(`Failed to fetch subscriptions: ${error.message}`);
  }
}

/**
 * Subscribe to real-time subscription updates
 * Listens for changes to the user's subscriptions
 */
export function subscribeToUserSubscriptions(
  userId: string,
  callback: (subscriptions: any[]) => void
) {
  const subscriptionsRef = collection(db, `customers/${userId}/subscriptions`);
  const q = query(subscriptionsRef, where('status', 'in', ['active', 'trialing']));
  
  return onSnapshot(q, (snapshot) => {
    const subscriptions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(subscriptions);
  });
}

/**
 * Get Stripe Customer Portal URL
 * 
 * NOTE: The extension automatically provides a 'stripeLink' field in subscription documents!
 * This field links directly to Stripe's Customer Portal where users can:
 * - Update payment methods
 * - View invoices
 * - Cancel subscriptions
 * - Upgrade/downgrade plans
 * 
 * Usage:
 * const subscription = await getDoc(doc(db, 'customers', userId, 'subscriptions', subId));
 * const portalUrl = subscription.data().stripeLink;
 * window.location.href = portalUrl;
 * 
 * Alternatively, you can create a portal session manually (below):
 */
export async function createPortalSession(userId: string, returnUrl: string) {
  try {
    const portalSessionRef = collection(
      db,
      `customers/${userId}/checkout_sessions`
    );

    const docRef = await addDoc(portalSessionRef, {
      return_url: returnUrl,
    });

    return new Promise<string>((resolve, reject) => {
      const unsubscribe = onSnapshot(
        docRef,
        (snap) => {
          const data = snap.data();
          if (data?.error) {
            unsubscribe();
            reject(new Error(data.error.message));
          }
          if (data?.url) {
            unsubscribe();
            resolve(data.url);
          }
        },
        reject
      );

      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        reject(new Error('Timeout waiting for portal URL from extension'));
      }, 10000);
    });
  } catch (error: any) {
    throw new Error(`Failed to create portal session: ${error.message}`);
  }
}

/**
 * Get payment method info from subscription's default_payment_method
 * The Stripe extension syncs this from the subscription object
 */
export function extractPaymentMethod(subscription: any): PaymentMethod | null {
  const pm = subscription?.default_payment_method;
  if (!pm?.card) return null;

  return {
    brand: pm.card.brand || 'card',
    last4: pm.card.last4 || '****',
    expMonth: pm.card.exp_month,
    expYear: pm.card.exp_year,
  };
}

/**
 * Get customer's payment history (invoices/payments)
 * Reads from customers/{userId}/payments collection
 */
export async function getCustomerPayments(userId: string, maxResults = 5): Promise<Invoice[]> {
  try {
    const paymentsRef = collection(db, `customers/${userId}/payments`);
    const q = query(
      paymentsRef,
      orderBy('created', 'desc'),
      firestoreLimit(maxResults)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        amount: data.amount || 0,
        currency: data.currency || 'aud',
        status: data.status || 'unknown',
        created: data.created?.toDate?.() || new Date(data.created?.seconds * 1000) || new Date(),
        invoiceUrl: data.charges?.data?.[0]?.receipt_url,
        invoicePdf: data.invoice_pdf,
      };
    });
  } catch (error: any) {
    console.error('Failed to fetch payments:', error);
    return [];
  }
}

/**
 * Subscribe to real-time payment updates
 */
export function subscribeToCustomerPayments(
  userId: string,
  callback: (payments: Invoice[]) => void,
  maxResults = 5
) {
  const paymentsRef = collection(db, `customers/${userId}/payments`);
  const q = query(
    paymentsRef,
    orderBy('created', 'desc'),
    firestoreLimit(maxResults)
  );

  return onSnapshot(q, (snapshot) => {
    const payments = snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        amount: data.amount || 0,
        currency: data.currency || 'aud',
        status: data.status || 'unknown',
        created: data.created?.toDate?.() || new Date(data.created?.seconds * 1000) || new Date(),
        invoiceUrl: data.charges?.data?.[0]?.receipt_url,
        invoicePdf: data.invoice_pdf,
      };
    });
    callback(payments);
  });
}

/**
 * Get all subscriptions including past_due/unpaid for status display
 */
export async function getAllUserSubscriptions(userId: string) {
  try {
    const subscriptionsRef = collection(db, `customers/${userId}/subscriptions`);
    const snapshot = await getDocs(subscriptionsRef);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error: any) {
    throw new Error(`Failed to fetch subscriptions: ${error.message}`);
  }
}

/**
 * Subscribe to all subscriptions (including problem statuses)
 */
export function subscribeToAllUserSubscriptions(
  userId: string,
  callback: (subscriptions: any[]) => void
) {
  const subscriptionsRef = collection(db, `customers/${userId}/subscriptions`);

  return onSnapshot(subscriptionsRef, (snapshot) => {
    const subscriptions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    callback(subscriptions);
  });
}